import clsx from 'clsx';
import { Award } from './Award';
import { IMAGE_LOADING, ImageFetchPriority } from '../image';
import type { AwardCollectionProps } from './types';

/**
 * AwardCollection Component
 *
 * This component is responsible for rendering a collection of awards.
 * It takes an array of award objects as a prop and maps over them to
 * generate individual Award components.
 *
 * Props:
 * - awards: An array of AwardNode objects, where each object contains
 *   the details of an award such as title and image.
 * - inKeyVisual: Optional boolean to indicate if the collection is rendered
 *   within a Key Visual.
 * - device: The device type (e.g., 'mobile' or 'desktop') for responsive
 *   rendering.
 *
 * Usage:
 * <AwardCollection awards={awardsArray} />
 *
 * The component ensures each Award is uniquely keyed using a combination
 * of its index and title.
 */
export function AwardCollection({ awards, device, inKeyVisual }: AwardCollectionProps) {
  const loading = inKeyVisual ? IMAGE_LOADING.EAGER : IMAGE_LOADING.LAZY;
  const fetchPriority = inKeyVisual ? ImageFetchPriority.High : ImageFetchPriority.Low;

  return (
    <ul className={clsx('awards-container', { 'awards-container--key-visual': inKeyVisual })}>
      {awards.map((award, index) =>
        award ? (
          <Award device={device} title={award?.title} image={award.image} loading={loading} fetchPriority={fetchPriority} key={`${index}-${award.title}`} />
        ) : null,
      )}
    </ul>
  );
}
